import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from 'src/environments/environment';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { WhatsappConfigService } from '../whatsapp-config/whatsapp-config.service';
import { WhatsAppConfigDTO } from '../whatsapp-config/whatsapp-config.interface';
import { WhatsappTemplateService } from '../whatsapp-template/whatsapp-template.service';
import { WhatsappTemplateItem } from '../whatsapp-template/whatsapp-template.interface';
import { WhatsappInboxService } from './whatsapp-inbox.service';
import { WhatsAppContactDTO, WhatsAppMediaView, WhatsAppMessageDTO } from './whatsapp-inbox.interface';

// WhatsApp Inbox: conversations stored by the WhatsAppWebhook service, replies sent through /NT/WAM/Send.
// ponytail: 5-second polling while the page is open; switch to a push channel if that is ever too slow or too chatty.
const POLL_MS = 5000;

@Component({
  selector: 'app-whatsapp-inbox',
  templateUrl: './whatsapp-inbox.component.html',
  styleUrls: ['./whatsapp-inbox.component.css']
})
export class WhatsappInboxComponent implements OnInit, OnDestroy {
  User = inject(UserLoginService).User();
  private inbox = inject(WhatsappInboxService);
  private configService = inject(WhatsappConfigService);
  private templateService = inject(WhatsappTemplateService);
  private route = inject(ActivatedRoute);

  @ViewChild('thread') thread?: ElementRef<HTMLDivElement>;

  configs: WhatsAppConfigDTO[] = [];
  selectedConfigId = 0;
  contacts: WhatsAppContactDTO[] = [];
  search = '';
  loadingContacts = false;

  selected: WhatsAppContactDTO | null = null;
  messages: WhatsAppMessageDTO[] = [];
  loadingMessages = false;
  private lastId = 0;
  private serverMark: string | null = null;   // newest messageOn/modifiedOn seen, so status changes are picked up by server time

  replyText = '';
  templates: WhatsappTemplateItem[] = [];
  selectedTemplate: WhatsappTemplateItem | null = null;
  templateValues: string[] = [];
  sending = false;
  sendError = '';

  private timer: any;

  // Attachments are fetched from Meta (through /NT/WAM/Media) only while a chat is open, keyed by waMessageId.
  media: Partial<Record<number, WhatsAppMediaView>> = {};

  ngOnInit(): void {
    this.configService.WhatsAppConfig_readAllPaginated({
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId,
      softwareId: Number(environment.SoftwareId),
      pageNo: 1,
      pageSize: 50
    } as any).subscribe(res => {
      // Only numbers connected through Meta Cloud API (empty WAUrl) have an inbox.
      this.configs = (res?.items ?? []).filter(c => !c.waUrl && !c.isDeleted);
      // ?waConfigId=… (from "Open inbox" on the connect page) picks that number; otherwise the first one.
      const wanted = Number(this.route.snapshot.queryParamMap.get('waConfigId'));
      const start = this.configs.find(c => c.waConfigId === wanted) ?? this.configs[0];
      if (start) this.selectConfig(start.waConfigId);
    });
    this.timer = setInterval(() => this.poll(), POLL_MS);
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    this.clearMedia();
  }

  selectConfig(waConfigId: number): void {
    this.selectedConfigId = Number(waConfigId);
    this.selected = null;
    this.messages = [];
    this.loadContacts(true);
    this.templateService.whatsappTemplate_ReadAllMetaTemplates({
      waConfigId: this.selectedConfigId, tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
    }).subscribe(res => this.templates = (res?.items ?? []).filter(t => t.status === 'APPROVED'));
  }

  loadContacts(showLoader = false): void {
    if (!this.selectedConfigId) return;
    this.loadingContacts = showLoader;
    this.inbox.contactList({
      tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId,
      waConfigId: this.selectedConfigId, search: this.search.trim()
    }).subscribe({
      next: res => {
        this.contacts = res?.items ?? [];
        if (this.selected) {
          const fresh = this.contacts.find(c => c.waContactId === this.selected!.waContactId);
          if (fresh) this.selected = fresh;
        }
        this.loadingContacts = false;
      },
      error: () => this.loadingContacts = false
    });
  }

  // Phones show either the list or the open chat; this takes the user back to the list.
  closeThread(): void {
    this.selected = null;
    this.messages = [];
    this.clearMedia();
  }

  open(contact: WhatsAppContactDTO): void {
    this.selected = contact;
    this.messages = [];
    this.clearMedia();
    this.lastId = 0;
    this.serverMark = null;
    this.replyText = '';
    this.sendError = '';
    this.selectedTemplate = null;
    this.loadingMessages = true;
    this.fetchMessages(true);
  }

  private fetchMessages(scroll: boolean): void {
    if (!this.selected) return;
    const contactId = this.selected.waContactId;
    this.inbox.messageList({
      tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId,
      waContactId: contactId, afterWAMessageId: this.lastId, statusSince: this.serverMark
    }).subscribe({
      next: res => {
        if (this.selected?.waContactId !== contactId) return;
        const rows = res?.items ?? [];
        let added = false;
        for (const m of rows) {
          const i = this.messages.findIndex(x => x.waMessageId === m.waMessageId);
          if (i >= 0) this.messages[i] = m; else { this.messages.push(m); added = true; }
          if (m.mediaId && !this.media[m.waMessageId] && this.autoLoads(m)) this.loadMedia(m);
          this.lastId = Math.max(this.lastId, m.waMessageId);
          const mark = m.modifiedOn && m.modifiedOn > m.messageOn ? m.modifiedOn : m.messageOn;
          if (!this.serverMark || mark > this.serverMark) this.serverMark = mark;
        }
        this.loadingMessages = false;
        if (added && this.selected.unreadCount > 0) this.markRead();
        if (added || scroll) setTimeout(() => this.scrollToBottom());
      },
      error: () => this.loadingMessages = false
    });
  }

  private markRead(): void {
    if (!this.selected) return;
    this.selected.unreadCount = 0;
    const c = this.contacts.find(x => x.waContactId === this.selected!.waContactId);
    if (c) c.unreadCount = 0;
    this.inbox.markRead({ tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId, waContactId: this.selected.waContactId }).subscribe();
  }

  private poll(): void {
    if (document.hidden || !this.selectedConfigId) return;
    this.loadContacts();
    if (this.selected && !this.loadingMessages) this.fetchMessages(false);
  }

  pickTemplate(name: string): void {
    this.selectedTemplate = this.templates.find(t => t.name === name) ?? null;
    const body = this.selectedTemplate?.components?.find((c: any) => c.type === 'BODY')?.text ?? '';
    const count = new Set((body.match(/\{\{\d+\}\}/g) ?? [])).size;
    this.templateValues = Array(count).fill('');
  }

  templatePreview(): string {
    let body = this.selectedTemplate?.components?.find((c: any) => c.type === 'BODY')?.text ?? '';
    this.templateValues.forEach((v, i) => body = body.split(`{{${i + 1}}}`).join(v || `{{${i + 1}}}`));
    return body;
  }

  get canSend(): boolean {
    if (!this.selected || this.sending) return false;
    return this.selected.isWindowOpen
      ? this.replyText.trim().length > 0
      : !!this.selectedTemplate && this.templateValues.every(v => v.trim().length > 0);
  }

  send(): void {
    if (!this.canSend || !this.selected) return;
    const useText = this.selected.isWindowOpen && this.replyText.trim().length > 0;
    this.sending = true;
    this.sendError = '';
    this.inbox.send({
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId,
      waContactId: this.selected.waContactId,
      text: useText ? this.replyText.trim() : undefined,
      templateName: useText ? undefined : this.selectedTemplate!.name,
      languageCode: useText ? undefined : this.selectedTemplate!.language,
      values: useText ? undefined : this.templateValues.map(v => v.trim()),
      actionUser: this.User.userId.toString()
    }).subscribe({
      next: res => {
        this.sending = false;
        if (!res?.success) { this.sendError = res?.message || 'Message was not sent.'; return; }
        this.replyText = '';
        this.selectedTemplate = null;
        this.templateValues = [];
        this.fetchMessages(true);
        this.loadContacts();
      },
      error: () => { this.sending = false; this.sendError = 'Message was not sent. Please try again.'; }
    });
  }

  onEnter(event: Event): void {
    const e = event as KeyboardEvent;
    if (!e.shiftKey) { e.preventDefault(); this.send(); }
  }

  windowHoursLeft(): number {
    if (!this.selected?.windowClosesOn) return 0;
    return Math.max(0, Math.round((new Date(this.selected.windowClosesOn).getTime() - Date.now()) / 3600000));
  }

  bubbleText(m: WhatsAppMessageDTO): string {
    if (m.msgType === 'template') return m.body ? m.body.split('|').join(' · ') : '';
    if (m.mediaId) return m.caption || '';
    return m.body ?? (m.msgType ? `[${m.msgType}]` : '');
  }

  sourceLabel(m: WhatsAppMessageDTO): string {
    return ({ ERP: 'ERP alert', Inbox: 'Inbox', App: 'From phone', History: 'History' } as any)[m.source] ?? '';
  }

  // Images, stickers and voice notes load as soon as the chat opens; videos and documents only on click.
  autoLoads(m: WhatsAppMessageDTO): boolean {
    return ['image', 'sticker', 'audio'].includes(m.msgType ?? '');
  }

  loadMedia(m: WhatsAppMessageDTO, then?: (url: string) => void): void {
    const contactId = this.selected?.waContactId;
    this.media[m.waMessageId] = { loading: true };
    this.inbox.media({ tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId, waMessageId: m.waMessageId })
      .subscribe({
        next: async blob => {
          if (this.selected?.waContactId !== contactId) return;
          // A JSON reply instead of a file means Meta no longer has it (kept for about 30 days).
          if (blob.type.includes('application/json')) {
            let error = 'File not available.';
            try { error = JSON.parse(await blob.text()).message || error; } catch { }
            this.media[m.waMessageId] = { loading: false, error };
            return;
          }
          const url = URL.createObjectURL(blob);
          this.media[m.waMessageId] = { loading: false, url };
          then?.(url);
          if (m.msgType !== 'document') setTimeout(() => this.scrollToBottom());
        },
        error: () => this.media[m.waMessageId] = { loading: false, error: 'Could not load the file. Click to retry.' }
      });
  }

  // Documents download under their own file name (fetched first if needed).
  downloadDocument(m: WhatsAppMessageDTO): void {
    const save = (url: string) => {
      const a = document.createElement('a');
      a.href = url;
      a.download = m.body || 'document';
      a.click();
    };
    const view = this.media[m.waMessageId];
    if (view?.url) save(view.url);
    else if (!view?.loading) this.loadMedia(m, save);
  }

  openInNewTab(url: string): void {
    window.open(url, '_blank');
  }

  private clearMedia(): void {
    Object.values(this.media).forEach(v => v?.url && URL.revokeObjectURL(v.url));
    this.media = {};
  }

  trackById(_: number, m: WhatsAppMessageDTO): number { return m.waMessageId; }

  private scrollToBottom(): void {
    const el = this.thread?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }
}
